import { FC, useEffect, useState } from 'react'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST, PAGE_LIMIT, QUERY_PARAMS } from '../../constants'
import { Maybe } from 'graphql/jsutils/Maybe'
import { Account, useFetchAllAccountsLazyQuery } from '../../../generated'
import { useDebounce } from 'use-debounce'
import { Autocomplete, TextField } from '@mui/material'

interface AccountsDropdownProps {
  setParams: (key: QUERY_PARAMS, value: string) => void
  accountId: string
  defaultSelectedAccountSortCode?: string
  isBankAccount?: boolean
  includeAllActiveAndInactiveAccounts?: boolean
  label?: string
  autoSelectOnTab?: boolean
  // Fills its container (e.g. a grid cell) instead of the fixed 300px width.
  fullWidth?: boolean
}

export const AccountsDropdown: FC<AccountsDropdownProps> = ({
  setParams,
  accountId,
  defaultSelectedAccountSortCode,
  isBankAccount,
  includeAllActiveAndInactiveAccounts,
  label,
  autoSelectOnTab = true,
  fullWidth = false
}) => {
  const [accounts, setAccounts] = useState<Maybe<Account>[]>([])
  const [accountPage, setAccountPage] = useState<number>(1)
  const [accountSearchTerm, setAccountSearchTerm] = useState<string>('')
  const [debouncedAccountSearchTerm] = useDebounce(accountSearchTerm, 500)

  const [fetchAllAccounts, { loading }] = useFetchAllAccountsLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onError(error) {
      console.log('error fetching accounts', error)
    },
    onCompleted(data) {
      const { fetchAllAccounts } = data || {}
      const { accounts, response } = fetchAllAccounts || {}
      if (response?.status === 200) {
        if (accountPage > 1) setAccounts(prev => [...prev, ...(accounts as Account[])])
        else setAccounts(accounts as Account[])
      }
    }
  })

  useEffect(() => {
    setAccountPage(1)
  }, [debouncedAccountSearchTerm])

  useEffect(() => {
    fetchAllAccounts({
      variables: {
        accountsInput: {
          paginationOptions: {
            page: accountPage,
            limit: PAGE_LIMIT
          },
          searchValue: debouncedAccountSearchTerm,
          isBankAccount,
          includeAllActiveAndInactiveAccounts
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountPage, debouncedAccountSearchTerm])

  const value: Maybe<Account> | null =
    (accountId ? accounts.find(account => account?.id === accountId) : null) ??
    (defaultSelectedAccountSortCode
      ? accounts.find(account => account?.sortCode === defaultSelectedAccountSortCode) ?? null
      : null) ??
    null

  useEffect(() => {
    if (!defaultSelectedAccountSortCode || accountId) return
    const defaultAccount = accounts.find(account => account?.sortCode === defaultSelectedAccountSortCode)
    if (defaultAccount?.id) {
      setParams(QUERY_PARAMS.ACCOUNT_ID, defaultAccount.id)
    }
  }, [defaultSelectedAccountSortCode, accounts, accountId, setParams])

  const handleAccountChange = (newValue: Maybe<Account>) => {
    setParams(QUERY_PARAMS.ACCOUNT_ID, newValue?.id || '')
  }

  return (
    <Autocomplete
      options={accounts}
      value={value}
      onChange={(_, newValue) => handleAccountChange(newValue)}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') {
          setAccountSearchTerm(newInputValue)
        } else if (reason === 'clear') {
          setAccountSearchTerm('')
        }
      }}
      getOptionLabel={option => `(#${option?.sortCode || ''}) ${option?.name || ''}`.trim()}
      loading={loading}
      autoHighlight={autoSelectOnTab}
      autoSelect={autoSelectOnTab}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderInput={params => <TextField {...params} label={label} placeholder="Select Account" />}
      ListboxProps={{
        onScroll: event => {
          const listboxNode = event.currentTarget
          if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 && !loading) {
            setAccountPage(prev => prev + 1)
          }
        }
      }}
      style={{
        width: fullWidth ? '100%' : 300
      }}
    />
  )
}
